import { Checkbox, Divider, Text, useBoolean, VStack } from '@chakra-ui/react';
import React from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';

import { AppError } from '../../../../../../framework/models/errors';
import { ListUtils } from '../../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../../framework/utils/UtilityTypes';
import AttachedFiles from '../../../../../components/AttachedFiles';
import { useCheckArea } from '../../../../../components/Check';
import { DecideButton } from '../../../../../components/StyledButton';
import { useGlobalStateMachine } from '../../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../../hooks/navigate';
import { usePostWorkerAssignOutsourceMember } from '../../../../../hooks/queries/workerAssign';
import useToastHook from '../../../../../hooks/ToastHook';
import {
  FileKind,
  UserSummary,
  WorkerAssignOutsourceMemberSearchResultData,
} from '../../../../../models/gen/models';
import { useSizeContext } from '../../../../SizeProvider';
import { WorkerAssignmentParams } from '../WorkerAssignment';
import FormArea, { WorkerAssignmentFormState } from './FormArea';

/**
 *
 */
type AssignFormProps = {
  members: UserSummary[];
  state?: WorkerAssignOutsourceMemberSearchResultData;
};

/**
 *
 * @returns
 */
const AssignForm: React.FunctionComponent<AssignFormProps> = (props) => {
  const { actions } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const params = useParams<WorkerAssignmentParams>();
  const form = useFormContext<WorkerAssignmentFormState>();
  const navigate = useEngeneNavigate();
  const { toast } = useToastHook();
  const { CheckArea, checked } = useCheckArea();

  const [checked1, { toggle: toggle1 }] = useBoolean();
  const [checked2, { toggle: toggle2 }] = useBoolean();
  const [checked3, { toggle: toggle3 }] = useBoolean();

  const [checkedFile, { toggle: toggleFile }] = useBoolean(false);

  const postWorkerAssignOutsourceMember = usePostWorkerAssignOutsourceMember();
  const onValid = React.useCallback<SubmitHandler<WorkerAssignmentFormState>>(
    (data) => {
      if (
        ListUtils.distinct(data.members, (member) => member.userCodeMember)
          .length !== data.members.length
      ) {
        toast('同じメンバが選択されています');
        return;
      }
      if (
        data.members.some((member) =>
          TypeUtils.isNilString(member.userCodeMember)
        )
      ) {
        toast('メンバが選択されていません');
        return;
      }
      actions.setGuard(true);
      postWorkerAssignOutsourceMember.mutate(
        {
          members: data.members.map((member) => ({
            assignWorkerId: member.assignWorkerId,
            userCodeMember: member.userCodeMember,
            isLeader: member.assignWorkerId.toString() === data.leader,
          })),
          message: data.message,
          dailyMatterId: parseInt(params.dailyMatterId!),
          fileAttached: checkedFile,
        },
        {
          onSuccess: () => {
            navigate();
            actions.setGuard(false);
          },
          onError: (error) => {
            toast(
              error instanceof AppError ? error : 'メンバアサインに失敗しました'
            );
            actions.setGuard(false);
          },
        }
      );
    },
    [params, actions, checkedFile]
  );

  return (
    <VStack as="form" onSubmit={form.handleSubmit(onValid)} width="100%">
      <FormArea members={props.members} state={props.state} />
      <VStack
        backgroundColor="white"
        borderColor="blackAlpha.300"
        borderWidth="1px"
        padding={2}
        width="full"
      >
        <AttachedFiles
          fileKind={FileKind.Message}
          files={props.state?.attachedFiles}
        />
        <Checkbox
          hidden={TypeUtils.isNilArray(props.state?.attachedFiles)}
          isChecked={checkedFile}
          onChange={toggleFile}
        >
          メールにファイルを添付します
        </Checkbox>
      </VStack>
      <CheckArea
        additionalArea={
          <>
            <VStack fontSize={sizes.fontSize.listMessage}>
              <Text fontWeight="bold">
                送信メッセージについて以下をチェックしてください
              </Text>
              <VStack
                alignItems="start"
                fontSize={sizes.fontSize.confirmMessage}
              >
                <Checkbox
                  isChecked={checked1}
                  onChange={toggle1}
                  textAlign="left"
                >
                  メッセージの内容を確認したこと
                </Checkbox>
                <Checkbox
                  isChecked={checked2}
                  onChange={toggle2}
                  textAlign="left"
                >
                  メンバの記載を確認したこと
                </Checkbox>
                <Checkbox
                  isChecked={checked3}
                  onChange={toggle3}
                  textAlign="left"
                >
                  メールアドレスのないメンバには送信されないこと
                </Checkbox>
              </VStack>
            </VStack>
            <Divider />
          </>
        }
        button={DecideButton}
        buttonProps={{
          isDisabled: !checked || !checked1 || !checked2 || !checked3,
        }}
        confirmMessage="上記メンバをアサインし、表示内容をメール送信します。"
      />
    </VStack>
  );
};

export default AssignForm;
